import { useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { CreateOrUpdateDataSetSchema } from 'libs/end-to-end/src/types/databases/data-sets/create-or-update-data-set-request-body';
import { OrganizationSecretItems } from 'libs/end-to-end/src/types/secrets/organization-secret-items';
import { DataSetType } from 'libs/end-to-end/src/types/databases/data-sets/data-sets';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { IntegrationType } from 'libs/end-to-end/src/types/databases/integrations/integration-type';
import { SecretReference } from 'libs/end-to-end/src/types/secrets/secret-reference';
import { SupplementalDatasetType } from 'libs/end-to-end/src/datasets';
import { Database } from 'libs/end-to-end/src/types/databases/database';
import { DataSet } from 'libs/end-to-end/src/types/databases/data-sets/data-set';
import { DataSetConfig } from 'libs/end-to-end/src/types/databases/data-sets/data-set';
import { DataSetWithExternalMergeData } from 'libs/end-to-end/src/types/databases/data-sets/data-set';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { SaveIndicatorStatus } from 'apps/sigma2-ui/src/components/SaveIndicator';
import { useSimpleReadWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadWritePattern';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { ReadPatternHook } from 'apps/sigma2-ui/src/lib/hooks/useReadPattern';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { validateHTTPIntegrationShared } from 'libs/end-to-end/src/types/databases/data-sets/validate-http-integration';
import { DataSetEditHTTPIntegrationHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditHTTPIntegration';
import { useDataSetEditHTTPIntegration } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditHTTPIntegration';
import { useDataSetEditPageAutoloadEffect } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditPageAutoloadEffect';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { MergeLinkToken } from 'libs/end-to-end/src/merge';
import { DataSetEditMergeIntegrationHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditMergeIntegration';
import { useDataSetEditMergeIntegration } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditMergeIntegration';
import { DataSetEditPostgresIntegrationHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditPostgresIntegration';
import { useDataSetEditPostgresIntegration } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditPostgresIntegration';
import { DataSetEditSqlServerIntegrationHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditSqlServerIntegration';
import { useDataSetEditSqlServerIntegration } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditSqlServerIntegration';
import { validatePostgresIntegrationShared } from 'libs/end-to-end/src/types/databases/data-sets/validate-postgres-integration';
import { DataSetIntegrationFieldValidationError } from 'libs/end-to-end/src/data-sets';
import { validateDataSetIntegrationFields } from 'libs/end-to-end/src/data-sets';
import { ResumeQualityScoreRubric } from 'libs/end-to-end/src/types/resume-parser/resume-quality-score-rubric';
import { ResumeParsingCacheBusterDefaultVersion } from 'libs/end-to-end/src/types/databases/integrations/resume-parsing-cache-buster';
import { DataSetEditCompositeHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditComposite';
import { useDataSetEditComposite } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditComposite';
import { AllowedOpenAiModels } from 'libs/end-to-end/src/types/databases/data-sets/data-set-resume-rubric';
import { useListOrganizationSecretsPattern } from 'apps/sigma2-ui/src/api/list-organization-secrets-pattern';
import { DbtAction } from 'libs/end-to-end/src/types/databases/integrations/snowflake-integration';
import { useDataSetEditSupplemental } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditSupplemental';
import { DataSetEditSupplementalHook } from 'apps/sigma2-ui/src/data-wizard/lib/useDataSetEditSupplemental';

export interface DataSetEditPageHook {
  databaseName: string;
  submitButtonLabel: string;
  submitButtonBehavior: TouchTargetBehavior;
  submitButtonDisabled: boolean;
  saveIndicatorStatus: SaveIndicatorStatus;
  backButtonLabel: string;
  backButtonBehavior: TouchTargetBehavior;
  backButtonDisabled: boolean;
  copyButtonLabel: string;
  copyButtonBehavior: TouchTargetBehavior;
  copyButtonDisabled: boolean;
  displayNameLabel: string;
  displayName: string;
  isArchived: boolean;
  setDisplayName: Setter<string>;
  setIsArchived: Setter<boolean>;
  typeLabel: string;
  integrationTypeLabel: string;
  supplementalDataSetTypeLabel: string;
  dataSetType: DataSetType;
  fileName: string;
  fileNameLabel: string;
  setFileName: Setter<string>;
  organizationId: string;
  integrationType: IntegrationType;
  supplementalDataSetType: SupplementalDatasetType;
  clientIdSecretReference: SecretReference | null;
  clientSecretSecretReference: SecretReference | null;
  usernameSecretReference: SecretReference | null;
  passwordSecretReference: SecretReference | null;
  folderIdSecretReference: SecretReference | null;
  fileNameSecretReference: SecretReference | null;
  oauthUrlSecretReference: SecretReference | null;
  setOauthUrlSecretReference: Setter<SecretReference | null>;
  setClientIdSecretReference: Setter<SecretReference | null>;
  setClientSecretSecretReference: Setter<SecretReference | null>;
  setUsernameSecretReference: Setter<SecretReference | null>;
  setPasswordSecretReference: Setter<SecretReference | null>;
  setFolderIdSecretReference: Setter<SecretReference | null>;
  setFileNameSecretReference: Setter<SecretReference | null>;
  apiKeySecretReference: SecretReference | null;
  setApiKeySecretReference: Setter<SecretReference | null>;
  listSecretsPattern: ReadPatternHook<OrganizationSecretItems>;
  compensationChangeHistoryUrlSecretReference: SecretReference | null;
  candidatesUrlSecretReference: SecretReference | null;
  employeeHistoryUrlSecretReference: SecretReference | null;
  staffingEventsUrlSecretReference: SecretReference | null;
  setCandidatesUrlSecretReference: Setter<SecretReference | null>;
  setCompensationChangeHistoryUrlSecretReference: Setter<SecretReference | null>;
  setEmployeeHistoryUrlSecretReference: Setter<SecretReference | null>;
  setStaffingEventsUrlSecretReference: Setter<SecretReference | null>;
  apiBaseUrlSecretReference: SecretReference | null;
  setApiBaseUrlSecretReference: Setter<SecretReference | null>;
  isLoading: boolean;
  httpIntegrationHook: DataSetEditHTTPIntegrationHook;
  postgresIntegrationHook: DataSetEditPostgresIntegrationHook;
  sqlServerIntegrationHook: DataSetEditSqlServerIntegrationHook;
  mergeIntegrationHook: DataSetEditMergeIntegrationHook;
  compositeHook: DataSetEditCompositeHook;
  supplementalHook: DataSetEditSupplementalHook;
  tryCreateMergeMagicLink: () => void;
  integrationFieldValidationErrors: DataSetIntegrationFieldValidationError[];
  resumeGradingRubric: ResumeQualityScoreRubric[];
  setResumeGradingRubric: Setter<ResumeQualityScoreRubric[]>;
  isResumeGradingEnabled: boolean;
  setIsResumeGradingEnabled: Setter<boolean>;
  resumeParsingCacheBusterVersion: string;
  setResumeParsingCacheBusterVersion: Setter<string>;
  externalMergeLink: string | null;
  openAiModel: AllowedOpenAiModels;
  setOpenAiModel: Setter<AllowedOpenAiModels>;
  sslCertSecretReference: SecretReference | null;
  keyCertSecretReference: SecretReference | null;
  setSslCertSecretReference: Setter<SecretReference | null>;
  setKeyCertSecretReference: Setter<SecretReference | null>;
  databaseId: string;
  dataSetId: string;
  dbtAction: DbtAction;
  setDbtAction: Setter<DbtAction>;
  dataSetVersionId: string | undefined;
  agentMode: boolean | null;
  setAgentMode: Setter<boolean>;
  agentDateCap: string;
  setAgentDateCap: Setter<string>;
}

export const useDataSetEditPage = (): DataSetEditPageHook => {
  const { organizationId, databaseId, dataSetId, dataSetVersionId } =
    useParams();
  const { userAuthenticatedApiCall } = useUser();
  const navigate = useNavigateSafe();

  if (!organizationId || !databaseId || !dataSetId) {
    throw new Error('organizationId and databaseId and dataSetId are required');
  }

  const listSecretsPattern = useListOrganizationSecretsPattern({
    organizationId,
  });

  const [displayName, setDisplayName] = useState('');
  const [isArchived, setIsArchived] = useState(false);
  const [dataSetType, setDataSetType] = useState<DataSetType>('File');
  const [fileName, setFileName] = useState('');
  const [agentMode, setAgentMode] = useState(false);
  const [agentDateCap, setAgentDateCap] = useState('7');

  const [integrationType, setIntegrationType] =
    useState<IntegrationType>('ShareFile');
  const httpIntegrationHook = useDataSetEditHTTPIntegration();
  const postgresIntegrationHook = useDataSetEditPostgresIntegration();
  const sqlServerIntegrationHook = useDataSetEditSqlServerIntegration();
  const mergeIntegrationHook = useDataSetEditMergeIntegration();
  const compositeHook = useDataSetEditComposite();
  const supplementalHook = useDataSetEditSupplemental();
  const [supplementalDataSetType, setSupplementalDataSetType] =
    useState<SupplementalDatasetType>('external_pay_benchmarking');
  const [clientIdSecretReference, setClientIdSecretReference] =
    useState<SecretReference | null>(null);
  const [clientSecretSecretReference, setClientSecretSecretReference] =
    useState<SecretReference | null>(null);
  const [oauthUrlSecretReference, setOauthUrlSecretReference] =
    useState<SecretReference | null>(null);
  const [apiBaseUrlSecretReference, setApiBaseUrlSecretReference] =
    useState<SecretReference | null>(null);
  const [sslCertSecretReference, setSslCertSecretReference] =
    useState<SecretReference | null>(null);
  const [keyCertSecretReference, setKeyCertSecretReference] =
    useState<SecretReference | null>(null);
  const [usernameSecretReference, setUsernameSecretReference] =
    useState<SecretReference | null>(null);
  const [passwordSecretReference, setPasswordSecretReference] =
    useState<SecretReference | null>(null);
  const [folderIdSecretReference, setFolderIdSecretReference] =
    useState<SecretReference | null>(null);
  const [fileNameSecretReference, setFileNameSecretReference] =
    useState<SecretReference | null>(null);
  const [apiKeySecretReference, setApiKeySecretReference] =
    useState<SecretReference | null>(null);
  const [
    compensationChangeHistoryUrlSecretReference,
    setCompensationChangeHistoryUrlSecretReference,
  ] = useState<SecretReference | null>(null);
  const [candidatesUrlSecretReference, setCandidatesUrlSecretReference] =
    useState<SecretReference | null>(null);
  const [
    employeeHistoryUrlSecretReference,
    setEmployeeHistoryUrlSecretReference,
  ] = useState<SecretReference | null>(null);
  const [
    staffingEventsUrlSecretReference,
    setStaffingEventsUrlSecretReference,
  ] = useState<SecretReference | null>(null);
  const [resumeGradingRubric, setResumeGradingRubric] = useState<
    ResumeQualityScoreRubric[]
  >([]);
  const [isResumeGradingEnabled, setIsResumeGradingEnabled] = useState(false);

  const [resumeParsingCacheBusterVersion, setResumeParsingCacheBusterVersion] =
    useState(ResumeParsingCacheBusterDefaultVersion);

  const [externalMergeLink, setExternalMergeLink] = useState<string | null>(
    null,
  );

  const [openAiModel, setOpenAiModel] =
    useState<AllowedOpenAiModels>('gpt-4o-mini');

  const [dbtAction, setDbtAction] = useState<DbtAction>('');

  const config = useMemo(() => {
    return createConfig({
      dataSetType,
      fileName,
      integrationType,
      usernameSecretReference,
      clientIdSecretReference,
      fileNameSecretReference,
      clientSecretSecretReference,
      oauthUrlSecretReference,
      apiBaseUrlSecretReference,
      folderIdSecretReference,
      passwordSecretReference,
      apiKeySecretReference,
      candidatesUrlSecretReference,
      compensationChangeHistoryUrlSecretReference,
      employeeHistoryUrlSecretReference,
      staffingEventsUrlSecretReference,
      httpIntegrationHook,
      postgresIntegrationHook,
      sqlServerIntegrationHook,
      mergeIntegrationHook,
      compositeHook,
      supplementalHook,
      sslCertSecretReference,
      keyCertSecretReference,
      organizationId,
      dbtAction,
      databaseId,
      dataSetId,
      dataSetVersionId: dataSetVersionId ?? '',
      agentMode,
      agentDateCap,
    });
  }, [
    dataSetType,
    fileName,
    integrationType,
    usernameSecretReference,
    clientIdSecretReference,
    fileNameSecretReference,
    clientSecretSecretReference,
    oauthUrlSecretReference,
    apiBaseUrlSecretReference,
    folderIdSecretReference,
    passwordSecretReference,
    apiKeySecretReference,
    candidatesUrlSecretReference,
    supplementalHook,
    compensationChangeHistoryUrlSecretReference,
    employeeHistoryUrlSecretReference,
    staffingEventsUrlSecretReference,
    httpIntegrationHook,
    postgresIntegrationHook,
    sqlServerIntegrationHook,
    mergeIntegrationHook,
    compositeHook,
    sslCertSecretReference,
    keyCertSecretReference,
    organizationId,
    dbtAction,
    databaseId,
    dataSetId,
    dataSetVersionId,
    agentMode,
    agentDateCap,
  ]);

  const integrationFieldValidationErrors = useMemo(() => {
    if (config.dataSetType !== 'Integration') {
      return [];
    }

    return validateDataSetIntegrationFields({
      integration: config.integration,
    });
  }, [config]);

  // eslint-disable-next-line complexity
  const validFormState = useMemo(() => {
    if (dataSetType === 'File') {
      return displayName.length > 0 && fileName.length > 0;
    }

    if (dataSetType === 'Supplemental') {
      if (
        supplementalDataSetType === 'resumes' &&
        isResumeGradingEnabled &&
        resumeGradingRubric.length === 0
      ) {
        return false;
      }
      return displayName.length > 0;
    }

    if (
      dataSetType === 'Integration' &&
      integrationFieldValidationErrors.length > 0
    ) {
      return false;
    }

    if (dataSetType === 'Integration') {
      if (integrationType === 'Merge') {
        return displayName.length > 0;
      }

      if (integrationType === 'ShareFile') {
        return (
          displayName.length > 0 &&
          clientIdSecretReference !== null &&
          clientSecretSecretReference !== null &&
          oauthUrlSecretReference !== null &&
          apiBaseUrlSecretReference !== null &&
          usernameSecretReference !== null &&
          passwordSecretReference !== null &&
          folderIdSecretReference !== null &&
          fileNameSecretReference !== null
        );
      }

      if (integrationType === 'Greenhouse') {
        return displayName.length > 0 && apiKeySecretReference !== null;
      }

      if (integrationType === 'ADP') {
        return (
          displayName.length > 0 &&
          clientIdSecretReference !== null &&
          clientSecretSecretReference !== null &&
          sslCertSecretReference !== null &&
          keyCertSecretReference !== null
        );
      }

      if (integrationType === 'Workday') {
        return (
          displayName.length > 0 &&
          usernameSecretReference !== null &&
          passwordSecretReference !== null &&
          employeeHistoryUrlSecretReference !== null &&
          staffingEventsUrlSecretReference !== null &&
          compensationChangeHistoryUrlSecretReference !== null &&
          candidatesUrlSecretReference !== null
        );
      }

      if (integrationType === 'HTTP') {
        const errors = validateHTTPIntegrationShared({
          integration: httpIntegrationHook.httpIntegration,
        });
        return errors.length === 0;
      }

      if (integrationType === 'Postgres') {
        const errors = validatePostgresIntegrationShared({
          integration: postgresIntegrationHook.postgresIntegration,
        });
        return errors.length === 0;
      }

      if (integrationType === 'Snowflake') {
        return dbtAction !== null || dbtAction !== '';
      }

      if (integrationType === 'Peregrine') {
        return (
          displayName.length > 0 &&
          usernameSecretReference !== null &&
          passwordSecretReference !== null
        );
      }

      if (integrationType === 'SqlServer') {
        return (
          displayName.length > 0 &&
          sqlServerIntegrationHook.sqlServerIntegration.user !== undefined &&
          sqlServerIntegrationHook.sqlServerIntegration.password !== undefined
        );
      }

      throw guardExhaustive(integrationType);
    }

    if (dataSetType === 'Composite') {
      return true;
    }

    throw guardExhaustive(dataSetType);
  }, [
    dataSetType,
    integrationFieldValidationErrors.length,
    displayName.length,
    fileName,
    supplementalDataSetType,
    isResumeGradingEnabled,
    resumeGradingRubric.length,
    integrationType,
    clientIdSecretReference,
    clientSecretSecretReference,
    oauthUrlSecretReference,
    apiBaseUrlSecretReference,
    usernameSecretReference,
    passwordSecretReference,
    folderIdSecretReference,
    fileNameSecretReference,
    apiKeySecretReference,
    sslCertSecretReference,
    keyCertSecretReference,
    employeeHistoryUrlSecretReference,
    staffingEventsUrlSecretReference,
    compensationChangeHistoryUrlSecretReference,
    candidatesUrlSecretReference,
    httpIntegrationHook.httpIntegration,
    postgresIntegrationHook.postgresIntegration,
    sqlServerIntegrationHook.sqlServerIntegration,
    dbtAction,
  ]);

  const readDatabaseGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}`;
  }, [databaseId, organizationId]);

  const readDatabasePattern = useSimpleReadPattern({
    getUrlFn: readDatabaseGetUrlFn,
    responseSchema: Database,
    canRead: true,
  });

  const patternGetReadUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSetId}`;
  }, [dataSetId, databaseId, organizationId]);

  const patternGetWriteUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets/${dataSetId}`;
  }, [dataSetId, databaseId, organizationId]);

  const pattern = useSimpleReadWritePattern({
    getReadUrlFn: patternGetReadUrlFn,
    canWrite: validFormState,
    responseSchema: DataSetWithExternalMergeData,
    canRead: true,
    writeSchema: CreateOrUpdateDataSetSchema,
    getWriteUrlFn: patternGetWriteUrlFn,
    writeMethod: 'PUT',
  });

  const copyPatternGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets`;
  }, [organizationId, databaseId]);

  const copyPattern = useSimpleWritePattern({
    getUrlFn: copyPatternGetUrlFn,
    method: 'POST',
    writeSchema: CreateOrUpdateDataSetSchema,
    responseSchema: DataSet,
    canWrite:
      pattern.status === 'done' &&
      pattern.entity != null,
  });

  const tryCreateMergeMagicLink = useCallback(async () => {
    const mergeResult = await userAuthenticatedApiCall({
      responseSchema: MergeLinkToken,
      url: `${getApiBaseUrl()}/merge`,
      requestOptions: {
        method: 'POST',
        body: JSON.stringify({
          organizationId,
          dataSetId,
          category: mergeIntegrationHook.mergeIntegration.category,
        }),
      },
    });

    if (mergeResult === null || mergeResult.resultType === 'err') {
      return;
    }

    const data = mergeResult.value;

    mergeIntegrationHook.setMergeIntegration({
      ...mergeIntegrationHook.mergeIntegration,
      linkToken: data.linkToken,
      magicLinkUrl: data.magicLinkUrl,
    });

    if (!pattern.canWrite || !validFormState) {
      return;
    }

    pattern.saveEntity({
      displayName,
      config: createConfig({
        dataSetType,
        fileName,
        integrationType,
        usernameSecretReference,
        clientIdSecretReference,
        fileNameSecretReference,
        clientSecretSecretReference,
        oauthUrlSecretReference,
        apiBaseUrlSecretReference,
        folderIdSecretReference,
        passwordSecretReference,
        apiKeySecretReference,
        candidatesUrlSecretReference,
        compensationChangeHistoryUrlSecretReference,
        employeeHistoryUrlSecretReference,
        staffingEventsUrlSecretReference,
        httpIntegrationHook,
        postgresIntegrationHook,
        sqlServerIntegrationHook,
        mergeIntegrationHook,
        compositeHook,
        supplementalHook,
        sslCertSecretReference,
        keyCertSecretReference,
        organizationId,
        dbtAction,
        databaseId,
        dataSetId,
        dataSetVersionId: dataSetVersionId ?? '',
        agentMode,
        agentDateCap,
      }),
      isArchived: false,
    });
  }, [
    userAuthenticatedApiCall,
    organizationId,
    dataSetId,
    mergeIntegrationHook,
    pattern,
    validFormState,
    displayName,
    dataSetType,
    fileName,
    integrationType,
    usernameSecretReference,
    clientIdSecretReference,
    fileNameSecretReference,
    clientSecretSecretReference,
    oauthUrlSecretReference,
    apiBaseUrlSecretReference,
    folderIdSecretReference,
    passwordSecretReference,
    apiKeySecretReference,
    candidatesUrlSecretReference,
    compensationChangeHistoryUrlSecretReference,
    employeeHistoryUrlSecretReference,
    staffingEventsUrlSecretReference,
    httpIntegrationHook,
    postgresIntegrationHook,
    sqlServerIntegrationHook,
    compositeHook,
    supplementalHook,
    sslCertSecretReference,
    keyCertSecretReference,
    dbtAction,
    databaseId,
    dataSetVersionId,
    agentMode,
    agentDateCap,
  ]);

  useDataSetEditPageAutoloadEffect({
    setStaffingEventsUrlSecretReference,
    setEmployeeHistoryUrlSecretReference,
    setPasswordSecretReference,
    setUsernameSecretReference,
    setDataSetType,
    setFolderIdSecretReference,
    setSupplementalDataSetType,
    setClientIdSecretReference,
    setFileNameSecretReference,
    setFileName,
    setDisplayName,
    setIsArchived,
    pattern,
    httpIntegrationHook,
    postgresIntegrationHook,
    sqlServerIntegrationHook,
    setApiKeySecretReference,
    setApiBaseUrlSecretReference,
    setCandidatesUrlSecretReference,
    setCompensationChangeHistoryUrlSecretReference,
    setOauthUrlSecretReference,
    setClientSecretSecretReference,
    setIntegrationType,
    mergeIntegrationHook,
    compositeHook,
    supplementalHook,
    setExternalMergeLink,
    setSslCertSecretReference,
    setKeyCertSecretReference,
    setDbtAction,
    setAgentMode,
    setAgentDateCap,
  });

  useEffect(() => {
    if (pattern.status === 'done' && pattern.saved && pattern.entity) {
      navigate(
        `/organizations/${organizationId}/databases/${databaseId}/data-sets`,
      );
    }
  }, [pattern, navigate, organizationId, databaseId]);

  useEffect(() => {
    if (
      copyPattern.status === 'done' &&
      copyPattern.saved &&
      copyPattern.entity
    ) {
      navigate(
        `/organizations/${organizationId}/databases/${databaseId}/data-sets/edit/${copyPattern.entity.id}`,
      );
    }
  }, [copyPattern, navigate, organizationId, databaseId]);

  const isLoading = useMemo(() => {
    return (
      pattern.status !== 'done' ||
      readDatabasePattern.status !== 'done' ||
      listSecretsPattern.status !== 'done'
    );
  }, [pattern.status, readDatabasePattern.status, listSecretsPattern.status]);

  return useMemo(() => {
    return {
      databaseId,
      dataSetId,
      isLoading,
      displayNameLabel: 'Display Name',
      displayName,
      setDisplayName,
      isArchived,
      setIsArchived,
      agentMode,
      setAgentMode,
      agentDateCap,
      setAgentDateCap,
      integrationTypeLabel: 'Integration Type',
      supplementalDataSetTypeLabel: 'Supplemental Data Set Type',
      typeLabel: 'Data Set Type',
      databaseName:
        readDatabasePattern.status === 'done'
          ? readDatabasePattern.entity.name
          : '',
      saveIndicatorStatus: pattern.saveIndicatorStatus,
      submitButtonDisabled: !pattern.canWrite || !validFormState,
      submitButtonLabel: 'Save Data Set',
      submitButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          if (!pattern.canWrite || !validFormState) {
            return;
          }

          pattern.saveEntity({
            displayName,
            isArchived,
            config: {
              ...config,
              ...(isResumeGradingEnabled && {
                resumeQualityScoreUpdatedAt: Date.now(),
              }),
            },
          });
        },
      },
      backButtonDisabled: pattern.status === 'saving' || !validFormState,
      backButtonLabel: 'Back',
      backButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          navigate(
            `/organizations/${organizationId}/databases/${databaseId}/data-sets`,
          );
        },
      },
      copyButtonLabel: 'Copy data set',
      copyButtonBehavior: {
        type: 'onClick',
        onClick: () => {
          if (pattern.status !== 'done' || !pattern.entity) {
            return;
          }
          if (copyPattern.status === 'saving') {
            return;
          }
          copyPattern.saveEntity({
            displayName: `[Copy] ${pattern.entity.displayName}`,
            config: pattern.entity.config,
            postProcessingSettings: pattern.entity.postProcessingSettings ?? undefined,
            isArchived: false,
          });
        },
      },
      copyButtonDisabled:
        pattern.status !== 'done' ||
        !pattern.entity ||
        copyPattern.status === 'saving',
      setClientIdSecretReference,
      setClientSecretSecretReference,
      clientIdSecretReference,
      integrationType,
      fileName,
      clientSecretSecretReference,
      dataSetType,
      fileNameLabel: 'File Name',
      fileNameSecretReference,
      organizationId,
      setFileName,
      setFileNameSecretReference,
      setFolderIdSecretReference,
      setPasswordSecretReference,
      setUsernameSecretReference,
      supplementalDataSetType,
      folderIdSecretReference,
      passwordSecretReference,
      usernameSecretReference,
      apiKeySecretReference,
      setApiKeySecretReference,
      listSecretsPattern,
      setEmployeeHistoryUrlSecretReference,
      setCandidatesUrlSecretReference,
      candidatesUrlSecretReference,
      setCompensationChangeHistoryUrlSecretReference,
      employeeHistoryUrlSecretReference,
      setStaffingEventsUrlSecretReference,
      compensationChangeHistoryUrlSecretReference,
      staffingEventsUrlSecretReference,
      oauthUrlSecretReference,
      setOauthUrlSecretReference,
      setApiBaseUrlSecretReference,
      apiBaseUrlSecretReference,
      httpIntegrationHook,
      postgresIntegrationHook,
      sqlServerIntegrationHook,
      mergeIntegrationHook,
      compositeHook,
      supplementalHook,
      tryCreateMergeMagicLink,
      integrationFieldValidationErrors,
      resumeGradingRubric,
      setResumeGradingRubric,
      isResumeGradingEnabled,
      setIsResumeGradingEnabled,
      externalMergeLink,
      resumeParsingCacheBusterVersion,
      setResumeParsingCacheBusterVersion,
      openAiModel,
      setOpenAiModel,
      sslCertSecretReference,
      keyCertSecretReference,
      setSslCertSecretReference,
      setKeyCertSecretReference,
      dbtAction: dbtAction ?? '',
      setDbtAction,
      dataSetVersionId,
    };
  }, [
    databaseId,
    dataSetId,
    isLoading,
    displayName,
    isArchived,
    agentMode,
    agentDateCap,
    readDatabasePattern,
    pattern,
    copyPattern,
    validFormState,
    clientIdSecretReference,
    integrationType,
    fileName,
    clientSecretSecretReference,
    dataSetType,
    fileNameSecretReference,
    organizationId,
    supplementalDataSetType,
    folderIdSecretReference,
    passwordSecretReference,
    usernameSecretReference,
    apiKeySecretReference,
    listSecretsPattern,
    candidatesUrlSecretReference,
    employeeHistoryUrlSecretReference,
    compensationChangeHistoryUrlSecretReference,
    staffingEventsUrlSecretReference,
    oauthUrlSecretReference,
    apiBaseUrlSecretReference,
    httpIntegrationHook,
    postgresIntegrationHook,
    sqlServerIntegrationHook,
    mergeIntegrationHook,
    compositeHook,
    supplementalHook,
    tryCreateMergeMagicLink,
    integrationFieldValidationErrors,
    resumeGradingRubric,
    isResumeGradingEnabled,
    externalMergeLink,
    resumeParsingCacheBusterVersion,
    openAiModel,
    sslCertSecretReference,
    keyCertSecretReference,
    dbtAction,
    dataSetVersionId,
    config,
    navigate,
  ]);
};

// eslint-disable-next-line complexity
const createConfig = ({
  dataSetType,
  fileName,
  integrationType,
  clientIdSecretReference,
  clientSecretSecretReference,
  oauthUrlSecretReference,
  usernameSecretReference,
  passwordSecretReference,
  folderIdSecretReference,
  fileNameSecretReference,
  apiKeySecretReference,
  employeeHistoryUrlSecretReference,
  staffingEventsUrlSecretReference,
  compensationChangeHistoryUrlSecretReference,
  candidatesUrlSecretReference,
  apiBaseUrlSecretReference,
  httpIntegrationHook,
  postgresIntegrationHook,
  sqlServerIntegrationHook,
  mergeIntegrationHook,
  compositeHook,
  supplementalHook,
  sslCertSecretReference,
  keyCertSecretReference,
  organizationId,
  dbtAction,
  databaseId,
  dataSetId,
  dataSetVersionId,
  agentMode,
  agentDateCap,
}: {
  dataSetType: DataSetType;
  fileName: string;
  integrationType: IntegrationType;
  clientIdSecretReference: SecretReference | null;
  oauthUrlSecretReference: SecretReference | null;
  clientSecretSecretReference: SecretReference | null;
  usernameSecretReference: SecretReference | null;
  passwordSecretReference: SecretReference | null;
  folderIdSecretReference: SecretReference | null;
  fileNameSecretReference: SecretReference | null;
  apiKeySecretReference: SecretReference | null;
  employeeHistoryUrlSecretReference: SecretReference | null;
  staffingEventsUrlSecretReference: SecretReference | null;
  compensationChangeHistoryUrlSecretReference: SecretReference | null;
  candidatesUrlSecretReference: SecretReference | null;
  apiBaseUrlSecretReference: SecretReference | null;
  httpIntegrationHook: DataSetEditHTTPIntegrationHook;
  postgresIntegrationHook: DataSetEditPostgresIntegrationHook;
  mergeIntegrationHook: DataSetEditMergeIntegrationHook;
  sqlServerIntegrationHook: DataSetEditSqlServerIntegrationHook;
  compositeHook: DataSetEditCompositeHook;
  supplementalHook: DataSetEditSupplementalHook;
  sslCertSecretReference: SecretReference | null;
  keyCertSecretReference: SecretReference | null;
  organizationId: string;
  dbtAction: DbtAction;
  databaseId: string;
  dataSetId: string;
  dataSetVersionId: string;
  agentMode: boolean | null;
  agentDateCap: string;
}): DataSetConfig => {
  if (dataSetType === 'File') {
    return {
      dataSetType: 'File',
      fileName,
    };
  }

  if (dataSetType === 'Supplemental') {
    return supplementalHook.config;
  }

  if (dataSetType === 'Integration') {
    if (integrationType === 'ShareFile') {
      return {
        dataSetType: 'Integration',
        integration: {
          integrationType: 'ShareFile',
          usernameSecretReference: usernameSecretReference ?? undefined,
          passwordSecretReference: passwordSecretReference ?? undefined,
          folderIdSecretReference: folderIdSecretReference ?? undefined,
          clientIdSecretReference: clientIdSecretReference ?? undefined,
          clientSecretSecretReference: clientSecretSecretReference ?? undefined,
          fileNameSecretReference: fileNameSecretReference ?? undefined,
          oauthUrlSecretReference: oauthUrlSecretReference ?? undefined,
          apiBaseUrlSecretReference: apiBaseUrlSecretReference ?? undefined,
        },
      };
    }

    if (integrationType === 'Workday') {
      return {
        dataSetType: 'Integration',
        integration: {
          integrationType: 'Workday',
          usernameSecretReference: usernameSecretReference ?? undefined,
          passwordSecretReference: passwordSecretReference ?? undefined,
          candidatesUrlSecretReference:
            candidatesUrlSecretReference ?? undefined,
          compensationChangeHistoryUrlSecretReference:
            compensationChangeHistoryUrlSecretReference ?? undefined,
          employeeHistoryUrlSecretReference:
            employeeHistoryUrlSecretReference ?? undefined,
          staffingEventsUrlSecretReference:
            staffingEventsUrlSecretReference ?? undefined,
        },
      };
    }

    if (integrationType === 'Greenhouse') {
      return {
        dataSetType: 'Integration',
        integration: {
          integrationType: 'Greenhouse',
          apiKeySecretReference: apiKeySecretReference ?? undefined,
        },
      };
    }

    if (integrationType === 'ADP') {
      return {
        dataSetType: 'Integration',
        integration: {
          integrationType: 'ADP',
          clientIdSecretReference: clientIdSecretReference ?? undefined,
          clientSecretSecretReference: clientSecretSecretReference ?? undefined,
          sslCertSecretReference: sslCertSecretReference ?? undefined,
          keyCertSecretReference: keyCertSecretReference ?? undefined,
          agentMode: agentMode ?? false,
          agentDateCap: agentDateCap ?? 'all',
        },
      };
    }

    if (integrationType === 'HTTP') {
      return {
        dataSetType: 'Integration',
        integration: httpIntegrationHook.httpIntegration,
      };
    }

    if (integrationType === 'Merge') {
      return {
        dataSetType: 'Integration',
        integration: mergeIntegrationHook.mergeIntegration,
      };
    }

    if (integrationType === 'Postgres') {
      return {
        dataSetType: 'Integration',
        integration: postgresIntegrationHook.postgresIntegration,
      };
    }

    if (integrationType === 'SqlServer') {
      return {
        dataSetType: 'Integration',
        integration: sqlServerIntegrationHook.sqlServerIntegration,
      };
    }

    if (integrationType === 'Snowflake') {
      return {
        dataSetType: 'Integration',
        integration: {
          integrationType: 'Snowflake',
          organizationId: organizationId,
          databaseId: databaseId,
          dataSetId: dataSetId,
          versionId: dataSetVersionId,
          dbtAction: dbtAction,
        },
      };
    }

    if (integrationType === 'Peregrine') {
      return {
        dataSetType: 'Integration',
        integration: {
          integrationType: 'Peregrine',
          usernameSecretReference: usernameSecretReference ?? undefined,
          passwordSecretReference: passwordSecretReference ?? undefined,
        },
      };
    }

    throw guardExhaustive(integrationType);
  }

  if (dataSetType === 'Composite') {
    return {
      dataSetType: 'Composite',
      taggedDataSetReferences: compositeHook.config.taggedDataSetReferences,
      pythonScriptContents: compositeHook.config.pythonScriptContents,
      recipe: compositeHook.config.recipe,
      runOrchestrationOnBatch: compositeHook.config.runOrchestrationOnBatch,
      runScriptOnBatch: compositeHook.config.runScriptOnBatch,
    };
  }

  throw guardExhaustive(dataSetType);
};
